import { getCategoryDisplayLabel } from "../../helpers/utils";
import { ROLES } from "../../helpers/constants";
import {
  formatUccStatusDisplayLabel,
  UCC_STATUS,
} from "../AdminDashboard/uccStatusConstants";

const USER_TYPE_DISPLAY_LABELS = {
  Customer: "Standard",
};

const formatUserTypeLabel = (label) => {
  if (!label) {
    return null;
  }
  return USER_TYPE_DISPLAY_LABELS[label] || label;
};

/** Matches the Type column in PropertyUsersListTable. */
export const getUserDisplayTypeLabel = (user) => {
  if (!user) {
    return null;
  }

  if (user?.type === "lennar") {
    const baseType = formatUserTypeLabel(user?.users_type?.[0]);
    return baseType ? `${baseType} (${user.type})` : user.type;
  }

  const typeLabels = [
    ...(Array.isArray(user?.user_type) ? user.user_type : []),
    ...(Array.isArray(user?.users_type) ? user.users_type : []),
  ];

  // Same sidebar/list chip treatment as Investor / Professional / Service Provider.
  if (typeLabels.includes("Sub User")) {
    return "Sub User";
  }

  if (user?.user_type?.[0] === "Customer" && user?.investor_id) {
    return "Investor";
  }

  if (user?.user_type?.[0] === "Customer" && user?.county_user_id) {
    return "County Investor";
  }

  if (user?.source === "househappy") {
    return "HouseHappy";
  }

  return formatUserTypeLabel(user?.users_type?.[0] || user?.user_type?.[0]);
};

export const userHasPhoneTosConsent = (user) =>
  Boolean(user?.has_phone_tos_consent);

export const userQualifiesForVerificationBypass = (user) =>
  userHasPhoneTosConsent(user);

export const getUserCategoryLabel = (user) =>
  getCategoryDisplayLabel(user?.category || user?.product_tier);

const PROTECTION_TIER_KEYS = new Set([
  "basic_monitoring",
  "enhanced_monitoring",
  "complete_protection",
]);

/** Normalize API tier/category values to canonical protection tier keys. */
export const normalizeProtectionTier = (tier) => {
  if (tier == null || tier === "") {
    return null;
  }

  const normalized = String(tier)
    .toLowerCase()
    .trim()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_");

  if (normalized.includes("enhanced_monitoring")) {
    return "enhanced_monitoring";
  }

  return PROTECTION_TIER_KEYS.has(normalized) ? normalized : null;
};

const normalizeComplimentaryFlag = (value) => {
  if (value === true || value === 1) {
    return true;
  }
  if (value === false || value === 0) {
    return false;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1" || normalized === "yes") {
      return true;
    }
    if (normalized === "false" || normalized === "0" || normalized === "no") {
      return false;
    }
  }
  return null;
};

/** True when the user account category is Enhanced Monitoring. */
export const isEnhancedMonitoringUser = (user) =>
  normalizeProtectionTier(user?.category || user?.product_tier) ===
  "enhanced_monitoring";

/** Enhanced Monitoring account enrolled on the complimentary (free) plan. */
export const isComplimentaryEnhancedMonitoringUser = (user) =>
  isEnhancedMonitoringUser(user) &&
  normalizeComplimentaryFlag(user?.is_complimentary) === true;

/** Signup step after which the Enhanced Monitoring plan (free or paid) is settled. */
const BILLING_TAG_MIN_SIGNUP_STEP = 4;

/**
 * Free/Paid tag for Enhanced Monitoring users from `is_complimentary`.
 * true → Free, false → Paid. Only for enhanced monitoring; otherwise null.
 * Hidden until signup is past the payment step: before that no subscription
 * exists yet, so a complimentary (e.g. El Dorado) account would read "Paid".
 */
export const getUserEnhancedMonitoringBillingTag = (user) => {
  if (!isEnhancedMonitoringUser(user)) {
    return null;
  }
  if (!(Number(user?.json_last_step?.step) > BILLING_TAG_MIN_SIGNUP_STEP)) {
    return null;
  }

  const complimentary = normalizeComplimentaryFlag(user?.is_complimentary);
  if (complimentary === true) {
    return "Free";
  }
  if (complimentary === false) {
    return "Paid";
  }

  const tier = String(user?.category || user?.product_tier || "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");
  if (tier.includes("enhanced_monitoring_free")) {
    return "Free";
  }
  if (tier.includes("enhanced_monitoring_paid")) {
    return "Paid";
  }

  return null;
};

export const isCompleteProtectionProperty = (property) =>
  normalizeProtectionTier(property?.product_tier || property?.category) ===
  "complete_protection";

/** Unique protection tiers from workspace property rows (matches property tabs). */
export const getProtectionTiersFromProperties = (properties = []) => {
  if (!Array.isArray(properties)) {
    return [];
  }

  const tiers = new Set();
  properties.forEach((property) => {
    const tier = normalizeProtectionTier(
      property?.product_tier || property?.category,
    );
    if (tier) {
      tiers.add(tier);
    }
  });

  return [...tiers];
};

/**
 * Header protection chip: "Multiple Protections" when tiers differ across properties,
 * otherwise the single tier label or the user's account category.
 */
export const getUserProtectionCategoryLabel = (user, propertyListData) => {
  const properties =
    propertyListData?.success === true && Array.isArray(propertyListData?.data)
      ? propertyListData.data
      : [];

  const tiersFromProperties = getProtectionTiersFromProperties(properties);
  if (tiersFromProperties.length > 1) {
    return "Multiple Protections";
  }
  if (tiersFromProperties.length === 1) {
    return getCategoryDisplayLabel(tiersFromProperties[0]);
  }

  return getUserCategoryLabel(user);
};

export const userHasMixedProtections = (propertyListData) =>
  getProtectionTiersFromProperties(
    propertyListData?.success === true ? propertyListData?.data : [],
  ).length > 1;

/** True when the account or any workspace property uses Complete Protection (preventive). */
export const userHasCompleteProtection = (user, propertyListData) => {
  if (
    normalizeProtectionTier(user?.category || user?.product_tier) ===
    "complete_protection"
  ) {
    return true;
  }

  const properties =
    propertyListData?.success === true && Array.isArray(propertyListData?.data)
      ? propertyListData.data
      : [];

  return getProtectionTiersFromProperties(properties).includes(
    "complete_protection",
  );
};

/**
 * Property count aligned with workspace property tabs (deduped list when loaded).
 */
export const getWorkspacePropertyCount = (propertyListData, user) => {
  if (
    propertyListData?.success === true &&
    Array.isArray(propertyListData?.data)
  ) {
    return propertyListData.data.length;
  }

  return (
    Number(user?.properties_count ?? 0) +
    Number(user?.parent_properties_count ?? 0)
  );
};

export const formatPropertyCountChipLabel = (count) => {
  const normalized = Number(count) || 0;
  if (normalized <= 0) {
    return null;
  }
  return normalized === 1 ? "1 property" : `${normalized} properties`;
};

const trimText = (value) => {
  const text = typeof value === "string" ? value.trim() : "";
  return text !== "" ? text : null;
};

const joinNames = (...parts) => trimText(parts.filter(Boolean).join(" "));

/** Account username (first + last name). */
export const getUserAccountName = (user) => {
  if (!user) {
    return "-";
  }
  return joinNames(user.first_name, user.last_name) || "-";
};

/** Deed owner / trust name from property records on the user row. */
export const getUserOwnerName = (user) => {
  if (!user) {
    return null;
  }

  const firstOwnerName = joinNames(
    user.first_owner_first_name,
    user.first_owner_last_name,
  );
  if (firstOwnerName) {
    return firstOwnerName;
  }

  const trustName = trimText(user.first_trust_llc_name);
  if (trustName) {
    return trustName;
  }

  const secondOwnerName = joinNames(
    user.second_owner_first_name,
    user.second_owner_last_name,
  );
  if (secondOwnerName) {
    return secondOwnerName;
  }

  return (
    trimText(user.second_trust_llc_name) || trimText(user?.primary_owner_name)
  );
};

export const getUserPropertyCountLabel = (user) => {
  const count =
    (user?.properties_count || 0) + (user?.parent_properties_count || 0);
  if (!count) {
    return "-";
  }
  return count === 1 ? "1 Property" : `${count} Properties`;
};

export const getUserPropertyVerificationLabel = (user) => {
  const status = user?.property_verification_status;
  if (!status) {
    return null;
  }
  if (status === "verified") {
    return "Verified";
  }
  if (status === "pending") {
    return "Pending";
  }
  return null;
};

/** Program column: Basic, Enhanced Paid, Enhanced Free or Complete. */
export const getUserProgramLabel = (user) => {
  // A live Complete property wins over a Basic/Enhanced tier (same rule as the reports list).
  if (user?.has_complete_protection) {
    return "Complete";
  }

  const tier = normalizeProtectionTier(user?.category || user?.product_tier);

  if (tier === "basic_monitoring") {
    return "Basic";
  }
  if (tier === "enhanced_monitoring") {
    const complimentary = normalizeComplimentaryFlag(user?.is_complimentary);
    if (complimentary === true) {
      return "Enhanced Free";
    }
    return complimentary === false ? "Enhanced Paid" : "Enhanced";
  }
  if (tier === "complete_protection") {
    return "Complete";
  }

  // Legacy Complete Protection accounts have no product_tier.
  const hasProperties =
    (user?.properties_count || 0) + (user?.parent_properties_count || 0) > 0;
  if (hasProperties && !isPartnerWorkspaceUser(user)) {
    return "Complete";
  }
  return "-";
};

/** Lowest UCC status across the user's live Complete properties; only Complete has one. */
export const getUserUccStatusLabel = (user) => {
  const program = getUserProgramLabel(user);
  if (program === "-") {
    return "-";
  }
  if (program !== "Complete") {
    return "Not Applicable";
  }
  return user?.lowest_ucc_status
    ? formatUccStatusDisplayLabel(user.lowest_ucc_status)
    : "-";
};

/** Property status of the Complete property with the lowest UCC status; "-" for Basic/Enhanced. */
export const getUserPropertyStatusLabel = (user) =>
  getUserProgramLabel(user) === "Complete"
    ? trimText(user?.lowest_property_status) || "-"
    : "-";

export const getUserStatusLabel = (user) => {
  const status = trimText(user?.status);
  if (!status) {
    return "-";
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const SOURCE_DISPLAY_LABELS = {
  househappy: "HouseHappy",
  dickensoncountyva: "Dickenson County VA",
};

export const getUserSourceLabel = (user) => {
  if (!user) {
    return "-";
  }

  const source = trimText(user?.source);
  if (source) {
    return SOURCE_DISPLAY_LABELS[source.toLowerCase()] || source;
  }

  if (user?.county_investor_domain) {
    return user.county_investor_domain;
  }
  if (user?.county_details?.county_name) {
    return user.county_details?.county_name;
  }

  if (user?.provider?.domain) {
    return user.provider.domain;
  }

  return "DirectWeb";
};

export const getUserCreatedByLabel = (user) => {
  const creator = user?.createdBy || user?.created_by;
  if (!creator) {
    return null;
  }

  const name = joinNames(creator.first_name, creator.last_name);
  return name || null;
};

/** Prefer account name, then deed owner names. Never use property address here. */
export const getUserDisplayName = (user) => {
  if (!user) {
    return "-";
  }

  const accountName = getUserAccountName(user);
  if (accountName !== "-") {
    return accountName;
  }

  const ownerName = getUserOwnerName(user);
  if (ownerName) {
    return ownerName;
  }

  return trimText(user.full_name) || trimText(user.email) || "-";
};

/** user.role / user_type from the API is an array of role labels (e.g. ["Customer"]). */
export const getUserRoleLabels = (user) => {
  const roles = user?.role ?? user?.user_type ?? user?.users_type;
  if (!roles) {
    return [];
  }
  const normalized = Array.isArray(roles) ? roles : [roles];
  return [...new Set(normalized.filter(Boolean))];
};

export const userHasRole = (user, roleLabel) =>
  getUserRoleLabels(user).includes(roleLabel);

export const SERVICE_PROVIDER_TYPE_LABELS = [
  "Service Provider",
  "Title Company",
  "Escrow Company",
  "Lender Service Provider",
  "Real Estate Agent",
  "Notary",
  "Attorney",
  "Other Service Provider",
];

export const PROFESSIONAL_TYPE_LABELS = ["Lender", "Broker", "Other"];

const userHasPartnerTypeLabel = (user, typeLabels) =>
  getUserRoleLabels(user).some((label) => typeLabels.includes(label)) ||
  typeLabels.includes(user?.service_provider_type?.type) ||
  typeLabels.includes(user?.professional_type?.type);

export const isProfessionalUser = (user) =>
  userHasRole(user, ROLES.professional) ||
  user?.type === "professional" ||
  user?.user_type === "professional" ||
  userHasPartnerTypeLabel(user, PROFESSIONAL_TYPE_LABELS);

export const isServiceProviderUser = (user) =>
  userHasRole(user, ROLES.serviceProvider) ||
  user?.type === "service_provider" ||
  user?.user_type === "service_provider" ||
  userHasPartnerTypeLabel(user, SERVICE_PROVIDER_TYPE_LABELS);

export const isPartnerWorkspaceUser = (user) =>
  isProfessionalUser(user) || isServiceProviderUser(user);

export const canSwitchCustomerRole = (user) =>
  user?.json_last_step?.step_key === "SEND_WELCOME_EMAIL" &&
  userHasRole(user, ROLES.customer);

/** True when a sent postcard exists and the mailing address is not verified yet. */
export const userHasPostcardVerificationPending = (propertyListData) => {
  const rows = Array.isArray(propertyListData?.data)
    ? propertyListData.data
    : [];
  return rows.some((row) => {
    if (!isCompleteProtectionProperty(row)) {
      return false;
    }

    const mailing = row?.user_mailing_address;
    if (!mailing?.id || Number(mailing.is_verified) === 1) {
      return false;
    }
    const postcards =
      Array.isArray(mailing.postcards) && mailing.postcards.length
        ? mailing.postcards
        : mailing.postcard
          ? [mailing.postcard]
          : [];
    return postcards.length > 0;
  });
};

/** True when signup skipped verification questions or any property is still unverified. */
export const userHasSkippedSecurityQuestions = (propertyListData, user) => {
  if (user?.json_last_step?.step_key === "VERIFY_ANSWERS") {
    return false;
  }

  const rows = Array.isArray(propertyListData?.data)
    ? propertyListData.data
    : [];
  const hasPropertyList = propertyListData?.success === true && rows.length > 0;

  if (hasPropertyList) {
    if (rows.some((row) => Number(row?.is_questions_verified_by_admin) === 1)) {
      return false;
    }

    const needsValidation = rows.some((row) => {
      if (Number(row?.is_verified) === 0) {
        return true;
      }

      const uccStatus = String(row?.ucc_status || "").trim();
      return (
        uccStatus === UCC_STATUS.PENDING_SKIPPED_SECURITY_QUESTIONS ||
        uccStatus.toLowerCase().includes("skipped security")
      );
    });

    if (!needsValidation) {
      return false;
    }
  }

  if (user?.json_last_step?.step_key === "SKIP_VERIFY_ANSWERS") {
    return true;
  }

  if (!hasPropertyList) {
    return false;
  }

  return rows.some((row) => {
    if (Number(row?.is_verified) === 0) {
      return true;
    }

    const uccStatus = String(row?.ucc_status || "").trim();
    return (
      uccStatus === UCC_STATUS.PENDING_SKIPPED_SECURITY_QUESTIONS ||
      uccStatus.toLowerCase().includes("skipped security")
    );
  });
};

const normalizeTypeLabels = (record) => {
  if (!record) {
    return [];
  }

  const rawTypes =
    record?.types?.map((entry) => entry?.type) ||
    record?.user_type ||
    record?.users_type ||
    [];

  return [
    ...new Set(
      (Array.isArray(rawTypes) ? rawTypes : [rawTypes]).filter(Boolean),
    ),
  ];
};

const resolvePartnerTypeLabel = (typeLabels) => {
  if (typeLabels.some((type) => SERVICE_PROVIDER_TYPE_LABELS.includes(type))) {
    return "Service Provider";
  }

  if (typeLabels.some((type) => PROFESSIONAL_TYPE_LABELS.includes(type))) {
    return "Professional";
  }

  return "Partner";
};

const formatSetupPartner = (record, partnerType = null) => {
  const userId = record?.user_id || record?.id;
  const firstName = record?.first_name?.trim?.() || "";
  const lastName = record?.last_name?.trim?.() || "";

  if (!userId) {
    return null;
  }

  const typeLabels = normalizeTypeLabels(record);

  const resolvedPartnerType =
    partnerType || resolvePartnerTypeLabel(typeLabels);
  const isExternalLead = resolvedPartnerType === "Service Provider";

  return {
    user_id: userId,
    first_name: firstName,
    last_name: lastName,
    email: record?.email || null,
    partner_type: resolvedPartnerType,
    display_label: isExternalLead ? "External Lead" : null,
    is_external_lead: isExternalLead,
  };
};

export const isExternalLeadPartner = (partner) =>
  Boolean(
    partner?.is_external_lead ||
    partner?.display_label === "External Lead" ||
    partner?.partner_type === "Service Provider",
  );

export const getSetupPartnerChipLabel = (partner) => {
  if (!partner) {
    return null;
  }

  if (partner.display_label) {
    return partner.display_label;
  }

  if (isExternalLeadPartner(partner)) {
    return "External Lead";
  }

  const partnerName =
    `${partner.first_name || ""} ${partner.last_name || ""}`.trim();
  return partnerName
    ? `${partner.partner_type}: ${partnerName}`
    : partner.partner_type;
};

export const getSetupPartnerChipTooltip = (partner) => {
  if (!partner) {
    return "";
  }

  const partnerName =
    `${partner.first_name || ""} ${partner.last_name || ""}`.trim();
  const partnerDetail = [partnerName, partner.email]
    .filter(Boolean)
    .join(" · ");

  if (isExternalLeadPartner(partner)) {
    return partnerDetail
      ? `Referred by ${partnerDetail} — open account`
      : "Open referring servicer account";
  }

  return partnerDetail
    ? `Open partner account: ${partnerDetail}`
    : "Open partner account in admin";
};

/** Partner who referred or created this customer (professional / service provider). */
export const getUserSetupPartner = (user) => {
  if (!user) {
    return null;
  }

  if (user.setup_partner?.user_id) {
    return user.setup_partner;
  }

  if (user.referral_user) {
    return formatSetupPartner(user.referral_user);
  }

  if (
    user.professional?.id &&
    String(user.professional.id) !== String(user.user_id || user.id)
  ) {
    return formatSetupPartner(user.professional);
  }

  const creator = user.created_by || user.createdBy;
  const creatorId = creator?.id || creator?.user_id;
  if (creatorId) {
    const creatorTypes = normalizeTypeLabels(creator);
    const isPartnerCreator = creatorTypes.some(
      (type) =>
        SERVICE_PROVIDER_TYPE_LABELS.includes(type) ||
        PROFESSIONAL_TYPE_LABELS.includes(type),
    );

    if (isPartnerCreator) {
      return formatSetupPartner(creator);
    }
  }

  return null;
};

/** Hide setup-partner chip on partner profiles and self-referential rows. */
export const getApiLicensePartner = (user) =>
  user?.api_partner || user?.apiPartner || null;

export const getApiLicensePartnerLabel = (partner) => {
  if (!partner) {
    return null;
  }
  const name = partner.name || "API partner";
  return partner.owned_by === "equityprotect"
    ? `Referred by ${name}`
    : `Owned by ${name}`;
};

export const getApiLicensePartnerTooltip = (partner) => {
  if (!partner) {
    return "";
  }
  const owner =
    partner.owned_by === "equityprotect" ? "EquityProtect" : "Partner";
  const key = [partner.key_name, partner.key_last_four ? `****${partner.key_last_four}` : null]
    .filter(Boolean)
    .join(" · ");
  return [`Owned by ${owner}`, partner.email, key].filter(Boolean).join(" · ");
};

export const shouldShowSetupPartnerChip = (user) => {
  if (!user || isPartnerWorkspaceUser(user)) {
    return false;
  }

  const partner = getUserSetupPartner(user);
  if (!partner?.user_id) {
    return false;
  }

  const currentUserId = user.user_id || user.id;
  return String(partner.user_id) !== String(currentUserId);
};

export const getUserPendingUnlock = (user) => {
  const pendingUnlock = user?.pending_unlock;
  if (!pendingUnlock || pendingUnlock.status !== "pending") {
    return null;
  }
  return pendingUnlock;
};

export const shouldShowPendingUnlockChip = (user) =>
  Boolean(getUserPendingUnlock(user));

export const getPendingUnlockInitiatorName = (pendingUnlock) => {
  const initiator = pendingUnlock?.initiator;
  if (!initiator) {
    return "";
  }
  return `${initiator.first_name || ""} ${initiator.last_name || ""}`.trim();
};

export const getPendingUnlockChipTooltip = (pendingUnlock) => {
  const name = getPendingUnlockInitiatorName(pendingUnlock);
  if (!name) {
    return "Pending Unlock";
  }

  if (pendingUnlock.initiated_by_type === "admin") {
    return `Admin: ${name}`;
  }

  if (pendingUnlock.initiated_by_type === "user") {
    return `User: ${name}`;
  }

  if (pendingUnlock.initiated_by_type === "service_provider") {
    return name ? `Service Provider: ${name}` : "Open service provider account";
  }

  return name;
};

export const isPendingUnlockServiceProviderInitiated = (pendingUnlock) =>
  pendingUnlock?.initiated_by_type === "service_provider" &&
  Boolean(pendingUnlock?.initiator?.user_id);
